import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DateObject from "react-date-object";
import { MdKeyboardArrowUp } from "react-icons/md";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import useDateBounds from "../../hooks/useDateBounds";

const PersianDatePicker = ({ onStartDateSelect, onEndDateSelect }) => {
  const [startDate, setStartDate] = useState(new DateObject({ calendar: persian, locale: persian_fa }));
  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [endDate, setEndDate] = useState(new DateObject({ calendar: persian, locale: persian_fa }));
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);
  const startCalendarRef = useRef(null);
  const endCalendarRef = useRef(null);
  // Bounds come from the backend, which derives the ceiling from the newest
  // hourly data and narrows it by any policy an admin assigned to this user.
  const { minDate, maxDate, dateLimit, loading, error } = useDateBounds();

  const getEndMaxDate = (startDate) => {
    if (!maxDate) return undefined;
    const limitReached = new DateObject(startDate).add(dateLimit, "days");
    return limitReached.valueOf() > maxDate.valueOf() ? maxDate : limitReached;
  };

  // The API speaks Gregorian-free ISO-ish "YYYY-MM-DD" built from the Jalali
  // parts, and every call site needs the same shape.
  const format = (d) =>
    `${d.year}-${String(d.month.number).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;

  // `add` mutates, so the DateObject has to be copied before shifting it.
  const nextDay = (d) => new DateObject(d).add(1, 'days');

  // The backend rejects start == end outright (`start_date < end_date`) and
  // treats the upper bound as exclusive, so a one-day selection still has to
  // span two dates. Both pickers default to the same day, which meant the very
  // first query of every session came back 400 "Invalid date range" -- and
  // because that left `hasFetchedDataSuccessfully` false, the whole
  // hourly/daily/monthly option panel never rendered at all.
  const validRange = (start) => {
    if (!maxDate) return [start, nextDay(start)];
    return start.valueOf() < maxDate.valueOf()
      ? [start, nextDay(start)]
      : [new DateObject(maxDate).subtract(1, 'days'), new DateObject(maxDate)];
  };

  // Both pickers default to today, but the data lags roughly a week behind, so
  // today normally sits past maxDate. Pull the selection back inside the range
  // once we know it -- otherwise the very first query is out of bounds.
  useEffect(() => {
    if (!minDate || !maxDate) return;

    const clamp = (d) => {
      if (d.valueOf() > maxDate.valueOf()) return maxDate;
      if (d.valueOf() < minDate.valueOf()) return minDate;
      return d;
    };

    // Pushed to the parent unconditionally rather than only when the clamp
    // moved something: the parent starts with its own defaults, and if today
    // happened to fall inside the window nothing was ever propagated, so the
    // first request used dates the picker had never agreed to.
    const [start, end] = validRange(clamp(startDate));
    setStartDate(start);
    setEndDate(end);
    onStartDateSelect(format(start));
    onEndDateSelect(format(end));
    // Runs when bounds land; selection changes afterwards are already in range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDate, maxDate]);


  const extractDateDetails = (dateDetails) => {
    const { day, month, year } = dateDetails;

    const formattedDay = String(day).padStart(2, '0');
    const formattedMonth = String(month).padStart(2, '0');
    return `${year}-${formattedMonth}-${formattedDay}`;
  };

  const handleClickOutside = (event) => {
    if (
      startCalendarRef.current && !startCalendarRef.current.contains(event.target) &&
      endCalendarRef.current && !endCalendarRef.current.contains(event.target)
    ) {
      setIsStartCalendarOpen(false);
      setIsEndCalendarOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStartSelect = (date) => {
    const [start, end] = validRange(new DateObject(date));
    setStartDate(start);
    setEndDate(end);

    onStartDateSelect(format(start));
    // The end date is propagated too. It used to be moved in local state only,
    // so the field on screen showed the new day while the parent still held the
    // previous one -- the request went out with a range the user never picked,
    // and nothing on screen contradicted it.
    onEndDateSelect(format(end));
    setIsStartCalendarOpen(false);
  };

  const handleEndSelect = (date) => {
    const newEndDate = new DateObject(date);
    setEndDate(newEndDate);
    onEndDateSelect(format(newEndDate));
    setIsEndCalendarOpen(false);
  };

  const formatDate = (date) => {
    const day = String(date.day).padStart(2, '0');
    const month = String(date.month.number).padStart(2, '0');
    const year = date.year;
    return `${year}/${month}/${day}`;
  };

  // Until the bounds arrive there is no valid range to pick from, so the
  // calendars stay shut rather than offering dates the backend would reject.
  const ready = !loading && !error && minDate && maxDate;

  if (!ready) {
    return (
      <div className="my-6 bg-white rounded-lg p-2 text-center text-sm text-gray-500">
        {error ? 'خطا در دریافت بازه‌ی مجاز تاریخ' : 'در حال دریافت بازه‌ی مجاز تاریخ...'}
      </div>
    );
  }

  return (
    <>
      <div className="relative my-6 flex items-center justify-start" ref={startCalendarRef}>
        <div className="flex w-full justify-between cursor-pointer bg-white rounded-lg p-2" onClick={() => {
          if (!isEndCalendarOpen) setIsStartCalendarOpen(!isStartCalendarOpen);
        }}
        >
          <div className=''>
            <p>تاریخ شروع را انتخاب کنید: </p>
          </div>
          <div className='flex items-center justify-between'>
            <p>{formatDate(startDate)}</p>
            {isStartCalendarOpen ? (
              <MdKeyboardArrowUp />
            ) : (
              <MdOutlineKeyboardArrowDown />
            )}
          </div>
        </div>

        {isStartCalendarOpen && (
          <Calendar
            className='absolute h-fit left-0 top-5 mt-2 !z-[100000000]'
            value={startDate}
            onChange={handleStartSelect}
            calendar={persian}
            locale={persian_fa}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      </div>

      <div className="relative flex items-center justify-start" ref={endCalendarRef}>
        <div className="flex w-full justify-between cursor-pointer  bg-white rounded-lg p-2" onClick={() => {
          if (!isStartCalendarOpen) setIsEndCalendarOpen(!isEndCalendarOpen);
        }}
        >
          <div className='flex items-center justify-between'>
            <p>تاریخ پایان را انتخاب کنید: </p>
          </div>
          <div className='flex items-center justify-between'>
            <p>{formatDate(endDate)}</p>
            {isEndCalendarOpen ? (
              <MdKeyboardArrowUp />
            ) : (
              <MdOutlineKeyboardArrowDown />

            )}
          </div>
        </div>

        {isEndCalendarOpen && (
          <Calendar
            className='absolute h-fit left-0 top-5 mt-2 !z-[100000000]'
            value={endDate}
            onChange={handleEndSelect}
            calendar={persian}
            locale={persian_fa}
            minDate={nextDay(startDate)}
            maxDate={getEndMaxDate(startDate)}
          />
        )}
      </div>
    </>
  );
};

export default PersianDatePicker;
